import { TabNameEditor } from 'components/shared/DragAndDropWorkspace/TabsWorkspace/Tab/TabNameEditor';
import { IconButton } from 'components/shared/IconButton';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEssentialTabName } from 'routes/essential-workspace/hooks/use-tab-name';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { workspaceTabRenamed } from 'state/workspaces/workspace.actions';

type Props = {
  isEditingTabName: boolean;
  onCancelEditTabName: () => void;
  onEditTabName: () => void;
};

function TabNameEditorHint() {
  return (
    <small className="d-flex justify-content-start gap-3 ms-2">
      <FormattedMessage
        id="tabs.rename.hint"
        values={{
          key: k => <kbd>{k}</kbd>,
          comment: c => <span className="text-nowrap">{c}</span>,
          hint: h => <div className="d-flex align-items-center gap-1">{h}</div>,
        }}
      />
    </small>
  );
}

export function HeaderTabName({ isEditingTabName, onCancelEditTabName, onEditTabName }: Props) {
  const dispatch = useThunkDispatch();
  const activeTabId = useAssertedActiveTabId();
  const label = useEssentialTabName(activeTabId);
  const onTabRenamed = useCallback(
    (value: string | undefined) => {
      onCancelEditTabName();
      const text = value?.trim() ?? '';
      if (text.length > 0) {
        dispatch(workspaceTabRenamed(activeTabId, text, 'essential'));
        logAnalytics.trackEvent('Primary market essential', 'Rename tab');
      }
    },
    [dispatch, activeTabId, onCancelEditTabName],
  );
  const { formatMessage } = useIntl();

  return (
    <div className="flex-x gap-1 essential-heading">
      {isEditingTabName ? (
        <TabNameEditor name={label ?? undefined} onCancelRenaming={onCancelEditTabName} onTabRenamed={onTabRenamed}>
          <TabNameEditorHint />
        </TabNameEditor>
      ) : (
        <>
          <h6 data-e2e="header-tab-name-label">{label}</h6>
          <IconButton
            iconName="edit"
            ariaLabel={formatMessage({ id: 'essential.workspace.edit.quote.btn.ariaLabel' })}
            onClick={onEditTabName}
          />
        </>
      )}
    </div>
  );
}
